import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItemButton,
  IconButton,
  Tooltip,
  useTheme
} from "@mui/material";
import { ContentCopy, CheckCircle } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Link } from "react-router-dom";

function extractHeadings(markdown) {
  const headingLines = markdown.split("\n").filter((line) => line.match(/^##+\s/));
  return headingLines.map((line) => {
    const level = line.startsWith("###") ? 3 : 2;
    const text = line.replace(/^#+\s/, "").trim();
    const id = text.toLowerCase().replace(/[^\w]+/g, "-").replace(/(^-|-$)+/g, "");
    return { level, text, id };
  });
}

export default function MarkdownComponents({ markdown }) {
  const theme = useTheme();
  const headings = extractHeadings(markdown);
  const headingRefs = useRef({});
  const [copiedText, setCopiedText] = useState(null);

  headings.forEach((heading) => {
    headingRefs.current[heading.id] = headingRefs.current[heading.id] || React.createRef();
  });

  const handleTOCClick = (id) => {
    const ref = headingRefs.current[id];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const components = {
    h2: ({ node, ...props }) => {
      const text = String(props.children);
      const id = text.toLowerCase().replace(/[^\w]+/g, "-").replace(/(^-|-$)+/g, "");
      return (
        <Typography
          component="div"
          ref={headingRefs.current[id]}
          id={id}
          // variant="h5"
          fontWeight={600}
          sx={{ color: 'text.primary' }}
        >
          {props.children}
        </Typography>
      );
    },
    h3: ({ node, ...props }) => {
      const text = String(props.children);
      const id = text.toLowerCase().replace(/[^\w]+/g, "-").replace(/(^-|-$)+/g, "");
      return (
        <Typography
          component="div"
          ref={headingRefs.current[id]}
          id={id}
          variant="h6"
          fontWeight={500}
          sx={{ mt: 3, mb: 1, color: 'text.primary' }}
        >
          {props.children}
        </Typography>
      );
    },
    p: ({ node, ...props }) => (
      <Typography
        component="div"
        variant="body1"
        sx={{ fontSize: "16px", mb: 2, color: 'text.primary' }}
        {...props}
      />
    ),
    code: ({ node, inline, className, children, ...props }) => {
      const codeText = String(children).trim();
      if (inline) {
        return (
          <code
            style={{
              fontSize: "14px",
              backgroundColor: theme.palette.mode === "dark"
                ? theme.palette.grey[800]
                : theme.palette.grey[200],
              padding: "2px 6px",
              borderRadius: 4,
              fontFamily: "monospace",
              color: theme.palette.text.primary
            }}
            {...props}
          >
            {children}
          </code>
        );
      }

      return (
        <Box
          component="pre"
          sx={{
            position: "relative",
            fontSize: "14px",
            fontFamily: "monospace",
            bgcolor: theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[100],
            p: 2,
            mb: 3,
            borderRadius: 1,
            overflowX: "auto",
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <code style={{ color: theme.palette.text.primary }}>{codeText}</code>
          <Tooltip title={copiedText === codeText ? "Copied!" : "Copy"}>
            <IconButton
              size="small"
              onClick={() => handleCopy(codeText)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: theme.palette.background.paper,
                boxShadow: 1,
                '&:hover': { bgcolor: theme.palette.action.hover }
              }}
            >
              {copiedText === codeText ? (
                <CheckCircle fontSize="small" color="success" />
              ) : (
                <ContentCopy fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    a: ({ node, ...props }) => (
      <Link {...props} style={{ color: theme.palette.primary.main, textDecoration: 'underline' }} />
    ),
  };

  return (
    <Box
      sx={{
        display: "flex",
         maxWidth: '100%',
        mx: "auto",
        alignItems: "flex-start",
        bgcolor: 'background.default',
        // gap: 3
      }}
    >
      {/* Markdown Content */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'background.paper',
         
           maxWidth: '100%',
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={components}
        >
          {markdown}
        </ReactMarkdown>
      </Box>

      {/* TOC */}
      {/* {headings.length > 0 && (
        <Box
          sx={{
            width: 240,
            position: "sticky",
            top: 100,
            maxHeight: 'calc(100vh - 120px)',
            overflowY: "auto",
            borderLeft: `1px dashed ${theme.palette.divider}`,
            pl: 2
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}
          >
            On this page
          </Typography>
          <List dense>
            {headings.map((heading) => (
              <ListItemButton
                key={heading.id}
                onClick={() => handleTOCClick(heading.id)}
                sx={{
                  pl: heading.level === 3 ? 3 : 2,
                  borderRadius: 1,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: theme.palette.action.hover
                  }
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: heading.level === 2 ? 600 : 400,
                    fontSize: heading.level === 2 ? "15px" : "14px",
                    color: 'text.secondary'
                  }}
                >
                  {heading.text}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Box>
      )} */}
    </Box>
  );
}
